import { useFlags, useFlagsmith } from 'flagsmith/react';
import { IFlagsmithFeature } from 'flagsmith/types';
import { useEffect, useState } from 'react';

const FLAGSMITH_TIMEOUT_MS = 5_000;

const useTimeout = (
  condition: boolean,
  timeoutMs: number,
  errorMessage: string,
) => {
  const [timeoutError, setTimeoutError] = useState<Error | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (condition) {
        setTimeoutError(new Error(errorMessage));
      }
    }, timeoutMs);

    return () => clearTimeout(timeoutId);
  }, [condition, timeoutMs, errorMessage]);
  return timeoutError;
};

type FlagReturnType<T> =
  | { loading: true; error?: never; flagValue: T }
  | { loading: false; error: Error; flagValue: T }
  | { loading: false; error?: never; flagValue: T };

const useFlag = <T extends IFlagsmithFeature>(
  flag: string,
): FlagReturnType<T> => {
  const flagsmith = useFlagsmith();
  const flags = useFlags([flag]);
  const timeoutError = useTimeout(
    flagsmith.loadingState?.isLoading ?? false,
    FLAGSMITH_TIMEOUT_MS,
    'Flagsmith took too long to load',
  );

  const flagValue = flags[flag] as T;

  if (timeoutError) {
    return {
      loading: false,
      error: timeoutError,
      flagValue,
    };
  }

  if (flagsmith.loadingState?.isLoading) {
    return { loading: true, flagValue };
  }

  if (flagsmith.loadingState?.error) {
    return {
      loading: false,
      error: flagsmith.loadingState.error,
      flagValue,
    };
  }

  return { loading: false, flagValue };
};

export default useFlag;
